import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiActivity,
  FiBarChart2,
  FiClock,
  FiRefreshCw,
  FiSearch,
  FiShield,
  FiUsers,
} from "react-icons/fi";

import Footer from "../../../components/Footer/Footer";
import { LoadingButton } from "../../../components/loadingButtom";
import { ModalDesconectar } from "../../../components/modais/ModalDesconectar";
import Navbar from "../../../components/Navbar/Navbar";
import { Cliente } from "../ListarUsuarios/functions";
import { FunctionObterClientesPrivate } from "./functions";
import "./ListarClientesPrivate.css";

const ID_SERVICO_PRIVATE = 3;
const NUMERO_PAGINA = 1;
const TAMANHO_PAGINA = 1000;

const normalizarTexto = (valor?: string) =>
  (valor || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const obterIniciais = (nome?: string) => {
  const partes = (nome || "Cliente")
    .trim()
    .split(" ")
    .filter(Boolean)
    .slice(0, 2);

  return partes.map((parte) => parte[0]).join("").toUpperCase() || "CL";
};

const formatarAtualizacao = (data?: Date) => {
  if (!data) {
    return "Aguardando atualização";
  }

  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    day: "2-digit",
    month: "2-digit",
  }).format(data);
};

export const ListarClientesPrivate = () => {
  const [listaClientesPrivate, setListaClientesPrivate] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [erro, setErro] = useState<boolean>(false);
  const [desconectar, setDesconectar] = useState<boolean>(false);
  const [reload, setReload] = useState<boolean>(false);
  const [busca, setBusca] = useState<string>("");
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState<Date>();

  const navigate = useNavigate();

  useEffect(() => {
    let ativo = true;

    const { obterTodosOsClientesPrivate } = FunctionObterClientesPrivate({
      idServicoFinanceiro: ID_SERVICO_PRIVATE,
      numeroPagina: NUMERO_PAGINA,
      tamanhoPagina: TAMANHO_PAGINA,
      setLoading,
      setErro,
      setListaClientesPrivate,
      setDesconectar,
    });

    const carregarClientesPrivate = async () => {
      await obterTodosOsClientesPrivate();

      if (ativo) {
        setUltimaAtualizacao(new Date());
      }
    };

    carregarClientesPrivate();

    return () => {
      ativo = false;
    };
  }, [reload]);

  const clientesFiltrados = useMemo(() => {
    const termo = normalizarTexto(busca);

    return listaClientesPrivate.filter((cliente) => {
      if (!termo) {
        return true;
      }

      return (
        normalizarTexto(cliente.nome).includes(termo) ||
        normalizarTexto(cliente.email).includes(termo)
      );
    });
  }, [busca, listaClientesPrivate]);

  const recarregar = () => setReload((valorAtual) => !valorAtual);

  const abrirFechamento = (idCliente: string) => {
    navigate(`/fechamento/${idCliente}`);
  };

  const abrirHistorico = (cliente: Cliente) => {
    navigate(`/gerenciar/historico-fechamento/${cliente.idCredenciais}/${cliente.nome}`);
  };

  const renderizarAcoes = (cliente: Cliente) => (
    <div className="clientes-private-actions" aria-label={`Ações de ${cliente.nome}`}>
      <button
        type="button"
        aria-label={`Novo fechamento de ${cliente.nome}`}
        title="Novo fechamento"
        onClick={() => abrirFechamento(cliente.idCredenciais)}
      >
        <FiBarChart2 />
      </button>
      <button
        type="button"
        aria-label={`Histórico do fundo de ${cliente.nome}`}
        title="Histórico do fundo"
        onClick={() => abrirHistorico(cliente)}
      >
        <FiClock />
      </button>
    </div>
  );

  return (
    <>
      <Navbar />

      <main className="clientes-private-page">
        <section className="clientes-private-hero">
          <div className="clientes-private-hero-copy">
            <span>Private</span>
            <h1>Clientes Private</h1>
            <p>
              Acesse rapidamente fundos private, envie novos fechamentos e
              consulte o histórico operacional de cada cliente.
            </p>
          </div>

          <div className="clientes-private-metrics" aria-label="Resumo de clientes private">
            <div>
              <FiUsers />
              <span>Total</span>
              <strong>{listaClientesPrivate.length}</strong>
            </div>
            <div>
              <FiShield />
              <span>Serviço</span>
              <strong>Private</strong>
            </div>
            <div>
              <FiActivity />
              <span>Exibidos</span>
              <strong>{clientesFiltrados.length}</strong>
            </div>
          </div>
        </section>

        <section className="clientes-private-toolbar" aria-label="Filtros de clientes private">
          <label className="clientes-private-search">
            <FiSearch aria-hidden="true" />
            <input
              type="search"
              value={busca}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setBusca(event.target.value)
              }
              placeholder="Buscar por nome ou e-mail"
            />
          </label>

          <button
            type="button"
            className="clientes-private-refresh"
            onClick={recarregar}
            disabled={loading}
          >
            <FiRefreshCw />
            Atualizar
          </button>
        </section>

        <section className="clientes-private-content">
          <div className="clientes-private-content-header">
            <div>
              <span>Fundos</span>
              <h2>Base Private</h2>
            </div>
            <p>
              <FiClock />
              {clientesFiltrados.length} de {listaClientesPrivate.length} exibidos ·{" "}
              {formatarAtualizacao(ultimaAtualizacao)}
            </p>
          </div>

          {loading ? (
            <div className="clientes-private-state">
              <LoadingButton />
            </div>
          ) : erro ? (
            <div className="clientes-private-state error">
              <strong>Não foi possível carregar os clientes private.</strong>
              <button type="button" onClick={recarregar}>
                <FiRefreshCw />
                Tentar novamente
              </button>
            </div>
          ) : clientesFiltrados.length === 0 ? (
            <div className="clientes-private-state">
              <strong>Nenhum cliente private encontrado.</strong>
            </div>
          ) : (
            <>
              <div className="clientes-private-table-wrap">
                <table className="clientes-private-table">
                  <thead>
                    <tr>
                      <th>Cliente</th>
                      <th>E-mail</th>
                      <th>Serviço</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientesFiltrados.map((cliente) => (
                      <tr key={cliente.idCredenciais}>
                        <td>
                          <div className="clientes-private-client">
                            <span>{obterIniciais(cliente.nome)}</span>
                            <strong>{cliente.nome || "Cliente sem nome"}</strong>
                          </div>
                        </td>
                        <td>{cliente.email || "-"}</td>
                        <td>
                          <span className="clientes-private-badge">Private</span>
                        </td>
                        <td>{renderizarAcoes(cliente)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="clientes-private-mobile-list">
                {clientesFiltrados.map((cliente) => (
                  <article className="clientes-private-mobile-card" key={cliente.idCredenciais}>
                    <div className="clientes-private-mobile-card-header">
                      <span>{obterIniciais(cliente.nome)}</span>
                      <div>
                        <strong>{cliente.nome || "Cliente sem nome"}</strong>
                        <small>{cliente.email || "-"}</small>
                      </div>
                    </div>
                    <div className="clientes-private-mobile-card-footer">
                      <span className="clientes-private-badge">Private</span>
                      {renderizarAcoes(cliente)}
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}
        </section>
      </main>

      {desconectar && <ModalDesconectar modal={desconectar} />}
      <Footer />
    </>
  );
};
